import React from 'react';
import { Link } from 'react-router-dom';
import { FaCrown, FaCalendarAlt, FaClock, FaArrowRight } from 'react-icons/fa';

const SubscriptionCard = ({ subscription }) => {
  if (!subscription) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
        <div className="text-center py-8">
          <FaCrown className="text-5xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-4">Subscribe to a plan to access all features</p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            View Plans <FaArrowRight />
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = () => {
    if (subscription.isActive) {
      return (
        <span className="bg-success-100 text-success-700 px-3 py-1 rounded-full text-sm font-semibold">
          Active
        </span>
      );
    }
    return (
      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
        {subscription.status}
      </span>
    );
  };

  const progressPercentage = subscription.daysRemaining > 0
    ? Math.min(100, (subscription.daysRemaining / 30) * 100)
    : 0;

  return (
    <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl shadow-lg p-6 mb-8 text-white">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <FaCrown className="text-2xl text-yellow-300" />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-xl font-bold">{subscription.planName}</h3>
              {getStatusBadge()}
            </div>
            <p className="text-primary-100 text-sm">Your current subscription</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-primary-200" />
            <div>
              <p className="text-xs text-primary-200">Started</p>
              <p className="font-semibold">{formatDate(subscription.startDate)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FaClock className="text-primary-200" />
            <div>
              <p className="text-xs text-primary-200">Expires</p>
              <p className="font-semibold">{formatDate(subscription.endDate)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Days Remaining Progress */}
      <div className="mt-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-primary-100">Days Remaining</span>
          <span className="font-bold">{subscription.daysRemaining} days</span>
        </div>
        <div className="h-3 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Renewal CTA if expiring soon */}
      {subscription.daysRemaining <= 7 && subscription.isActive && (
        <div className="mt-4 p-4 bg-white/10 rounded-xl">
          <p className="text-sm mb-2">Your subscription is expiring soon!</p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-white text-primary-700 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
          >
            Renew Now <FaArrowRight />
          </Link>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
